/* eslint-disable no-magic-numbers */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tab, TabList, TabPanel, Tabs } from 'carbon-react/lib/components/tabs/__next__';
import Box from 'carbon-react/lib/components/box';

import {
  matchCreateConsolidationRoutePath,
  getCreateConsolidationUrl,
  getEditConsolidationUrl
} from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { CoaStructure } from '../consolidation-content/coa-structure/coa-structure';
import { DailyFxRates } from '../consolidation-content/daily-fx-rates/daily-fx-rates';
import { Eliminations } from '../consolidation-content/eliminations/eliminations';
import { Settings } from '../consolidation-content/settings/settings';
import { TopButtons } from './top-buttons/top-buttons';
import { useConsolidationContext } from '../consolidation-context/consolidation-context';
import { TABS } from '../constants';

import styles from './edit-consolidation.scss';

export const EditConsolidation = () => {
  const {
    config,
    flags: { isReady, isMultiCurrency }
  } = useConsolidationContext();
  const navigate = useNavigate();
  const intl = useIntl();

  const { workspaceId: tempWorkspaceId, organisationId: tempConsolidationId, tabId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const consolidationId = tempConsolidationId || '';

  const tabs = useMemo(() => {
    const tabs = [
      {
        id: TABS.SETTINGS,
        label: intl.formatMessage({
          id: 'consolidation.settings_tab',
          defaultMessage: '1. Settings'
        }),
        children: <Settings editing />
      },
      {
        id: TABS.ELIMINATIONS,
        label: intl.formatMessage({
          id: 'consolidation.eliminations_tab',
          defaultMessage: '2. Eliminations'
        }),
        children: <Eliminations editing />
      },
      {
        id: TABS.COA_STRUCTURE,
        label: intl.formatMessage({
          id: 'consolidation.chart_of_accounts_structure_tab',
          defaultMessage: '3. Chart of Accounts Structure'
        }),
        children: <CoaStructure />
      }
    ];

    if (isMultiCurrency) {
      tabs.push({
        id: TABS.DAILY_FX_RATES,
        label: intl.formatMessage({
          id: 'consolidation.daily_fx_rates_tab',
          defaultMessage: '4. Daily FX Rates'
        }),
        children: <DailyFxRates />
      });
    }

    return tabs;
  }, [intl, isMultiCurrency]);

  // ************
  // * Handlers *
  // ************

  const handleExit = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      const newPath = matchCreateConsolidationRoutePath(location.pathname)
        ? getCreateConsolidationUrl(workspaceId, tabId)
        : getEditConsolidationUrl(workspaceId, consolidationId, tabId);
      navigate(newPath, { replace: true });
    },
    [consolidationId, navigate, workspaceId]
  );

  // ***********
  // * Effects *
  // ***********

  // Redirect to first tab if none / invalid tab specified
  useEffect(() => {
    const invalidTab = !tabs.find(tab => tab.id === tabId);
    if (isReady && invalidTab) {
      handleTabChange(TABS.SETTINGS);
    }
  }, [isReady, tabId, tabs, handleTabChange]);

  if (!isReady) {
    return <UILoader isFull />;
  }

  return (
    <>
      <h1 className={styles.title}>
        <FormattedMessage
          id="consolidation.settings_title"
          defaultMessage="Consolidation settings: {name}"
          values={{ name: config?.name }}
        />
      </h1>

      <Box className={styles.carbonTabsContainer}>
        <Tabs orientation="horizontal" selectedTabId={tabId}>
          <TabList
            ariaLabel={intl.formatMessage({
              id: 'consolidation.tabs.aria_label',
              defaultMessage: 'Consolidation settings'
            })}
            onTabChange={handleTabChange}
          >
            {tabs.map(t => (
              <Tab key={t.id} id={t.id} controls={`${t.id}-panel`} label={t.label} />
            ))}
          </TabList>
          {tabs.map(t => (
            <TabPanel key={t.id} id={`${t.id}-panel`} tabId={t.id}>
              <div className={styles.tabContent}>
                <Box p="20px 0">
                  <TopButtons onExit={handleExit} />
                </Box>
                {t.children}
              </div>
            </TabPanel>
          ))}
        </Tabs>
      </Box>
    </>
  );
};
